import type {
  ActivationsPage,
  AuditLogsPage,
  CustomersPage,
  JobsPage,
} from '../types'
import { Box, ChevronLeft, ChevronRight, Laptop, RefreshCw, Search } from './Icons'

type PageFooterProps = {
  count: number
  total: number
  limit: number
  offset: number
  busy: boolean
  onPageChange: (offset: number) => void
}

function PageFooter({
  count,
  total,
  limit,
  offset,
  busy,
  onPageChange,
}: PageFooterProps) {
  const page = Math.floor(offset / limit) + 1
  const pages = Math.max(1, Math.ceil(total / limit))
  const start = total === 0 ? 0 : offset + 1
  const end = Math.min(offset + count, total)
  return (
    <footer className="table-footer">
      <span>
        Showing {start} to {end} of {total.toLocaleString()} results
      </span>
      <div className="pagination" aria-label="Pagination">
        <button
          type="button"
          aria-label="Previous page"
          disabled={page <= 1 || busy}
          onClick={() => onPageChange(Math.max(0, offset - limit))}
        >
          <ChevronLeft size={15} />
        </button>
        <span className="page-count">
          {page} / {pages}
        </span>
        <button
          type="button"
          aria-label="Next page"
          disabled={page >= pages || busy}
          onClick={() => onPageChange(offset + limit)}
        >
          <ChevronRight size={15} />
        </button>
      </div>
    </footer>
  )
}

function dateTimeLabel(value: string | null) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('en', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}

function SearchField({
  value,
  placeholder,
  onChange,
}: {
  value: string
  placeholder: string
  onChange: (value: string) => void
}) {
  return (
    <label className="table-search operation-search">
      <Search size={14} aria-hidden="true" />
      <input
        aria-label={placeholder}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  )
}

export function CustomersPanel({
  page,
  query,
  busy,
  onQueryChange,
  onPageChange,
  onOpenEntitlements,
}: {
  page: CustomersPage | null
  query: string
  busy: boolean
  onQueryChange: (value: string) => void
  onPageChange: (offset: number) => void
  onOpenEntitlements: (email: string) => void
}) {
  const rows = page?.items ?? []
  return (
    <section className="operation-page">
      <header className="page-toolbar">
        <div>
          <h2>Customers</h2>
          <p>Accounts grouped by verified owner email and owned products.</p>
        </div>
        <SearchField
          value={query}
          placeholder="Search customers or products"
          onChange={onQueryChange}
        />
      </header>
      <div className={`table-scroll ${busy ? 'is-loading' : ''}`} aria-busy={busy}>
        <table className="operation-table customer-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Products</th>
              <th>Entitlements</th>
              <th>Active devices</th>
              <th>Total seats</th>
              <th>Last activity</th>
              <th aria-label="Customer actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <strong>{row.name}</strong>
                  <span>{row.email}</span>
                </td>
                <td>
                  <div className="tag-list">
                    {row.products.map((product) => (
                      <span className="tag" key={product}>{product}</span>
                    ))}
                  </div>
                </td>
                <td>{row.active_entitlements} active · {row.entitlement_count} total</td>
                <td>{row.active_devices}</td>
                <td>{row.total_seats}</td>
                <td>{dateTimeLabel(row.last_activity_at)}</td>
                <td>
                  <button
                    className="table-action"
                    type="button"
                    onClick={() => onOpenEntitlements(row.email)}
                  >
                    View licenses
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {busy && page === null && (
          <div className="empty-state" role="status">Loading customers…</div>
        )}
        {!busy && rows.length === 0 && (
          <div className="empty-state">No customers match this search.</div>
        )}
      </div>
      <PageFooter
        count={rows.length}
        total={page?.total ?? 0}
        limit={page?.limit ?? 25}
        offset={page?.offset ?? 0}
        busy={busy}
        onPageChange={onPageChange}
      />
    </section>
  )
}

export function ActivationsPanel({
  page,
  query,
  status,
  products,
  product,
  busy,
  canDeactivate,
  canRevoke,
  onQueryChange,
  onStatusChange,
  onProductChange,
  onPageChange,
  onDeactivate,
  onRevoke,
}: {
  page: ActivationsPage | null
  query: string
  status: string
  products: string[]
  product: string
  busy: boolean
  canDeactivate: boolean
  canRevoke: boolean
  onQueryChange: (value: string) => void
  onStatusChange: (value: string) => void
  onProductChange: (value: string) => void
  onPageChange: (offset: number) => void
  onDeactivate: (activationId: string) => Promise<void>
  onRevoke: (activationId: string, reason: string) => Promise<void>
}) {
  const rows = page?.items ?? []
  return (
    <section className="operation-page">
      <header className="page-toolbar">
        <div>
          <h2>Activations</h2>
          <p>Device seats across every Curic product and SketchUp host.</p>
        </div>
        <div className="toolbar-controls">
          <SearchField value={query} placeholder="Search device or customer" onChange={onQueryChange} />
          <select aria-label="Product filter" value={product} onChange={(event) => onProductChange(event.target.value)}>
            <option value="">All products</option>
            {products.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <select aria-label="Activation status filter" value={status} onChange={(event) => onStatusChange(event.target.value)}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="deactivated">Deactivated</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>
      </header>
      <div className={`table-scroll ${busy ? 'is-loading' : ''}`} aria-busy={busy}>
        <table className="operation-table activation-table">
          <thead>
            <tr>
              <th>Device</th>
              <th>Customer</th>
              <th>Product</th>
              <th>Host</th>
              <th>Plugin</th>
              <th>Last seen</th>
              <th>Status</th>
              <th aria-label="Activation actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <span className="device-cell"><Laptop size={15} /> <strong>{row.device_name}</strong></span>
                  <span className="mono">{row.installation_id}</span>
                </td>
                <td><strong>{row.customer_name}</strong><span>{row.email}</span></td>
                <td><span className="product-cell"><Box size={15} />{row.product}</span></td>
                <td>{row.host_app} {row.host_version}<span>{row.platform}</span></td>
                <td className="mono">{row.plugin_version}</td>
                <td>{dateTimeLabel(row.last_seen_at)}</td>
                <td><span className="status-text"><i className={`square ${row.status === 'active' ? 'success' : 'muted'}`} />{row.status}</span></td>
                <td>
                  <span className="activation-actions">
                    {row.status === 'active' && canDeactivate ? (
                      <button
                        className="table-action danger-action"
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Deactivate ${row.device_name} for ${row.email}?`)) {
                            void onDeactivate(row.id)
                          }
                        }}
                      >Deactivate</button>
                    ) : null}
                    {row.status !== 'revoked' && canRevoke ? (
                      <button
                        className="table-action danger-action"
                        type="button"
                        onClick={() => {
                          const reason = window.prompt(
                            `Permanently revoke ${row.device_name}? Enter an audit reason (8–500 characters). Do not include license keys or other secrets.`,
                          )?.trim()
                          if (reason) void onRevoke(row.id, reason)
                        }}
                      >Revoke</button>
                    ) : null}
                    {!(row.status === 'active' && canDeactivate) && !(row.status !== 'revoked' && canRevoke) ? '—' : null}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="empty-state">No activations match these filters.</div>}
      </div>
      <PageFooter count={rows.length} total={page?.total ?? 0} limit={page?.limit ?? 25} offset={page?.offset ?? 0} busy={busy} onPageChange={onPageChange} />
    </section>
  )
}

export function JobsPanel({
  page,
  status,
  jobType,
  busy,
  canRetry,
  retryingId,
  onStatusChange,
  onJobTypeChange,
  onPageChange,
  onRetry,
}: {
  page: JobsPage | null
  status: string
  jobType: string
  busy: boolean
  canRetry: boolean
  retryingId: string | null
  onStatusChange: (value: string) => void
  onJobTypeChange: (value: string) => void
  onPageChange: (offset: number) => void
  onRetry: (jobId: string) => Promise<void>
}) {
  const rows = page?.items ?? []
  return (
    <section className="operation-page">
      <header className="page-toolbar">
        <div>
          <h2>Commerce & reconciliation</h2>
          <p>Webhook background jobs. Dead jobs require an explicit audited retry.</p>
        </div>
        <div className="toolbar-controls">
          <SearchField value={jobType} placeholder="Exact job type" onChange={onJobTypeChange} />
          <select aria-label="Job status filter" value={status} onChange={(event) => onStatusChange(event.target.value)}>
            <option value="">All statuses</option>
            <option value="dead">Dead</option>
            <option value="pending">Pending</option>
            <option value="running">Running</option>
            <option value="succeeded">Succeeded</option>
          </select>
        </div>
      </header>
      <div className={`table-scroll ${busy ? 'is-loading' : ''}`} aria-busy={busy}>
        <table className="operation-table jobs-table">
          <thead><tr><th>Job</th><th>Status</th><th>Attempts</th><th>Scheduled</th><th>Updated</th><th>Last error</th><th aria-label="Job actions" /></tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td><strong>{row.job_type}</strong><span className="mono">{row.id}</span></td>
                <td><span className="status-text"><i className={`square ${row.status === 'dead' ? 'danger' : row.status === 'succeeded' ? 'success' : 'muted'}`} />{row.status}</span></td>
                <td>{row.attempts} / {row.max_attempts}</td>
                <td>{dateTimeLabel(row.run_after)}</td>
                <td>{dateTimeLabel(row.updated_at)}</td>
                <td className="error-cell">{row.last_error ?? '—'}</td>
                <td>
                  {row.status === 'dead' && canRetry ? (
                    <button
                      className="table-action"
                      type="button"
                      disabled={retryingId !== null}
                      onClick={() => {
                        if (window.confirm(`Retry dead job ${row.id}? The action will be audited.`)) void onRetry(row.id)
                      }}
                    ><RefreshCw size={13} />{retryingId === row.id ? 'Retrying…' : 'Retry'}</button>
                  ) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="empty-state">No reconciliation jobs match these filters.</div>}
      </div>
      <PageFooter count={rows.length} total={page?.total ?? 0} limit={page?.limit ?? 50} offset={page?.offset ?? 0} busy={busy} onPageChange={onPageChange} />
    </section>
  )
}

export function AuditLogsPanel({
  page,
  query,
  outcome,
  busy,
  onQueryChange,
  onOutcomeChange,
  onPageChange,
}: {
  page: AuditLogsPage | null
  query: string
  outcome: string
  busy: boolean
  onQueryChange: (value: string) => void
  onOutcomeChange: (value: string) => void
  onPageChange: (offset: number) => void
}) {
  const rows = page?.items ?? []
  return (
    <section className="operation-page">
      <header className="page-toolbar">
        <div><h2>Audit log</h2><p>Immutable operator and system actions without sensitive payloads.</p></div>
        <div className="toolbar-controls">
          <SearchField value={query} placeholder="Actor, action, entity or request ID" onChange={onQueryChange} />
          <select aria-label="Audit outcome filter" value={outcome} onChange={(event) => onOutcomeChange(event.target.value)}>
            <option value="">All outcomes</option><option value="success">Success</option><option value="failure">Failure</option>
          </select>
        </div>
      </header>
      <div className={`table-scroll ${busy ? 'is-loading' : ''}`} aria-busy={busy}>
        <table className="operation-table audit-table">
          <thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Entity</th><th>Request ID</th><th>Outcome</th></tr></thead>
          <tbody>{rows.map((row) => (
            <tr key={row.id}>
              <td>{dateTimeLabel(row.occurred_at)}</td><td>{row.actor}</td><td><strong>{row.action}</strong></td>
              <td><span>{row.entity_type}</span><span className="mono">{row.entity_id}</span></td>
              <td className="mono">{row.request_id ?? '—'}</td>
              <td><span className="status-text"><i className={`square ${row.outcome === 'success' ? 'success' : 'danger'}`} />{row.outcome}</span></td>
            </tr>
          ))}</tbody>
        </table>
        {rows.length === 0 && <div className="empty-state">No audit events match this search.</div>}
      </div>
      <PageFooter count={rows.length} total={page?.total ?? 0} limit={page?.limit ?? 50} offset={page?.offset ?? 0} busy={busy} onPageChange={onPageChange} />
    </section>
  )
}
